/**
 * TreeView Component
 *
 * Hierarchical navigation panel for browsing structure tree.
 */

/**
 * TreeView displays a collapsible tree of all structures.
 */
export class TreeView {
  /**
   * Create tree view
   * @param {string|HTMLElement} container - Container element or ID
   * @param {Object} cellMap - CellMap instance for navigation
   */
  constructor(container, cellMap) {
    this.container =
      typeof container === 'string'
        ? document.getElementById(container)
        : container;
    this.cellMap = cellMap;
    this.structureLayer = null;

    this.element = null;
    this.treeRoot = null;
    this.closeBtn = null;
    this.toggleBtn = null;

    this.expandedNodes = new Set();
    this.selectedId = null;

    this.render();
    this.bindEvents();
  }

  /**
   * Render the tree view
   */
  render() {
    this.element = document.createElement('aside');
    this.element.className = 'tree-view';
    this.element.setAttribute('aria-label', 'Inhaltsverzeichnis');
    this.element.innerHTML = `
      <div class="tree-view-header">
        <h3 class="tree-view-title">Inhaltsverzeichnis</h3>
        <button class="tree-view-close" aria-label="Schließen">&times;</button>
      </div>
      <div class="tree-view-content">
        <ul class="tree-root" role="tree" aria-label="Strukturen"></ul>
      </div>
    `;

    this.treeRoot = this.element.querySelector('.tree-root');
    this.closeBtn = this.element.querySelector('.tree-view-close');

    this.container.appendChild(this.element);

    // Toggle button (outside the panel)
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'tree-view-toggle';
    this.toggleBtn.innerHTML = '📋';
    this.toggleBtn.setAttribute('aria-label', 'Inhaltsverzeichnis anzeigen');
    this.toggleBtn.setAttribute('title', 'Inhaltsverzeichnis');
    this.container.appendChild(this.toggleBtn);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    this.closeBtn.addEventListener('click', () => this.close());
    this.toggleBtn.addEventListener('click', () => this.toggle());

    // Keyboard: Escape to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  /**
   * Initialize tree after structures are loaded
   */
  init() {
    this.structureLayer = this.cellMap.getStructureLayer();
    if (!this.structureLayer) {
      console.error('TreeView: Structure layer not available');
      return;
    }

    // Build tree from root structures (parentId = null)
    const structures = this.structureLayer.getAllStructures();
    const roots = structures.filter((s) => s.parentId === null);

    // Sort roots alphabetically
    roots.sort((a, b) => a.name.localeCompare(b.name, 'de'));

    roots.forEach((root) => {
      const node = this.createNode(root);
      this.treeRoot.appendChild(node);
    });

    console.info(`TreeView initialized with ${roots.length} root structures`);
  }

  /**
   * Create a tree node element
   * @param {Object} structure - Structure object
   * @returns {HTMLElement}
   */
  createNode(structure) {
    const li = document.createElement('li');
    li.className = 'tree-node';
    li.dataset.id = structure.id;
    li.setAttribute('role', 'treeitem');

    const children = this.structureLayer.getChildren(structure.id);
    const hasChildren = children.length > 0;

    li.innerHTML = `
      <div class="tree-node-content">
        ${
          hasChildren
            ? `
          <button class="tree-node-toggle" aria-expanded="false" aria-label="Unterelemente ${structure.name}">
            <span class="tree-node-icon">▶</span>
          </button>
        `
            : `
          <span class="tree-node-spacer"></span>
        `
        }
        <button class="tree-node-label">${this.escapeHtml(structure.name)}</button>
      </div>
      ${
        hasChildren
          ? `
        <ul class="tree-node-children" role="group" hidden></ul>
      `
          : ''
      }
    `;

    // Toggle handler
    const toggle = li.querySelector('.tree-node-toggle');
    if (toggle) {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleNode(structure.id, li);
      });
    }

    // Navigate handler
    const label = li.querySelector('.tree-node-label');
    label.addEventListener('click', () => {
      this.navigateTo(structure.id);
    });

    return li;
  }

  /**
   * Toggle node expand/collapse
   * @param {number} id - Structure ID
   * @param {HTMLElement} nodeEl - Node element
   */
  toggleNode(id, nodeEl) {
    const isExpanded = this.expandedNodes.has(id);
    const toggle = nodeEl.querySelector('.tree-node-toggle');
    const childrenContainer = nodeEl.querySelector('.tree-node-children');

    if (!toggle || !childrenContainer) {
      return;
    }

    if (isExpanded) {
      // Collapse
      this.expandedNodes.delete(id);
      toggle.setAttribute('aria-expanded', 'false');
      toggle.querySelector('.tree-node-icon').textContent = '▶';
      childrenContainer.hidden = true;
    } else {
      // Expand
      this.expandedNodes.add(id);
      toggle.setAttribute('aria-expanded', 'true');
      toggle.querySelector('.tree-node-icon').textContent = '▼';
      childrenContainer.hidden = false;

      // Lazy load children if not yet loaded
      if (childrenContainer.children.length === 0) {
        const children = this.structureLayer.getChildren(id);
        // Sort children alphabetically
        children.sort((a, b) => a.name.localeCompare(b.name, 'de'));
        children.forEach((child) => {
          const childNode = this.createNode(child);
          childrenContainer.appendChild(childNode);
        });
      }
    }
  }

  /**
   * Navigate to a structure
   * @param {number} id - Structure ID
   */
  navigateTo(id) {
    const clickHandler = this.cellMap.getClickHandler();
    if (clickHandler) {
      clickHandler.selectById(id);
    }
  }

  /**
   * Highlight currently selected structure
   * @param {number|null} id - Structure ID or null
   */
  setSelected(id) {
    // Remove previous selection
    this.treeRoot.querySelectorAll('.tree-node--selected').forEach((el) => {
      el.classList.remove('tree-node--selected');
    });

    this.selectedId = id;

    if (id === null) {
      return;
    }

    // Expand ancestors to show selection
    this.expandToNode(id);

    // Add new selection
    const node = this.treeRoot.querySelector(`[data-id="${id}"]`);
    if (node) {
      node.classList.add('tree-node--selected');

      // Scroll into view
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * Expand tree to show a specific node
   * @param {number} id - Structure ID
   */
  expandToNode(id) {
    const ancestors = this.structureLayer.getAncestorTree(id);

    // Expand all ancestors (except the target itself)
    ancestors.slice(0, -1).forEach((ancestor) => {
      if (!this.expandedNodes.has(ancestor.id)) {
        const nodeEl = this.treeRoot.querySelector(`[data-id="${ancestor.id}"]`);
        if (nodeEl) {
          this.toggleNode(ancestor.id, nodeEl);
        }
      }
    });
  }

  /**
   * Open the tree panel
   */
  open() {
    this.element.classList.add('tree-view--open');
    this.toggleBtn.classList.add('tree-view-toggle--active');
    this.toggleBtn.setAttribute('aria-pressed', 'true');
  }

  /**
   * Close the tree panel
   */
  close() {
    this.element.classList.remove('tree-view--open');
    this.toggleBtn.classList.remove('tree-view-toggle--active');
    this.toggleBtn.setAttribute('aria-pressed', 'false');
  }

  /**
   * Toggle panel visibility
   */
  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Check if panel is open
   * @returns {boolean}
   */
  isOpen() {
    return this.element.classList.contains('tree-view--open');
  }

  /**
   * Get the element
   * @returns {HTMLElement}
   */
  getElement() {
    return this.element;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
